<template>
  <div :class="$style.container">
    <NotificationInPage
      :class="$style.notification"
      :data-test-id="$testId('settingsBanner')"
      :notification="$options.notifications.notification"
      :showNotification="showNotification"
      @close="handleClose()"
    />
    <div :class="$style.list">
      <div
        v-for="(component, index) in stateComponents"
        :key="`component.type-${index}`"
      >
        <ul :class="$style.header">
          <li>
            <a
              :class="[$style.headerText, {
                [$style.activeHeaderText]: isActiveSelection(index),
              }]"
              :data-test-id="$testId(component.label)"
              @click="handleClick(component.type, index)"
            >
              {{ component.label }}
            </a>
            <span :class="$style.count">
              {{ component.count }}
            </span>
          </li>
        </ul>
      </div>
      <div>
        <IconButton
          hasNoBackground
          icon="settings"
          text="Settings"
          :to="$options.settingsRoute"
        />
      </div>
    </div>
    <LoadingOverlay
      :showLoadingOverlay="showLoadingOverlay"
      :zIndex="Number(itemTableZIndex) + 1"
    />
  </div>
</template>

<script>
import * as Constants from 'client/util/Constants';
import {
  IconButton,
  NotificationInPage,
  Types,
} from '@jumpcloud/ui-components';
import { capitalize, get } from 'lodash';
import { itemTableZIndex } from 'client/css/z-index.css';
import { mapActions, mapGetters, mapState } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import LoadingOverlay from 'client/components/LoadingOverlay/LoadingOverlay.vue';
import LocalStorageService from 'client/util/LocalStorageService';
import NumberSeparatorUtil from 'client/util/NumberSeparatorUtil';

const {
  LocalStorageConsts,
  ModuleConsts,
} = Constants;

const organizationsCollection = 'OrganizationsCollection';
const systemUserCollection = 'SystemUserCollection';
const { NotificationConfig } = Types;

const notifications = {
  notification: NotificationConfig({
    icon: 'info',
    isMarkdown: false,
    isSingleLineAction: true,
    showCloseButton: true,
    message: 'Set the default user state for different creation methods by clicking the Settings button below.',
    title: '',
    isFullWidth: true,
    type: 'informational',
    '_constructor-name_': 'NotificationConfig',
  }),
};

const settingsModule = 'SettingsModule';

const { activated, staged, suspended } = ModuleConsts.systemUser.accountStates;
const allUsers = 'All';

export default {
  name: 'SystemUserFilter',

  components: {
    IconButton,
    LoadingOverlay,
    NotificationInPage,
  },

  data() {
    return {
      activeSelection: 0,
      itemTableZIndex,
      showLoadingOverlay: false,
      showNotification: false,
    };
  },

  computed: {
    ...mapState(settingsModule, ['orgId']),

    ...mapGetters(organizationsCollection, ['getItemDetails']),

    ...mapGetters(systemUserCollection, [
      'getAppliedFilters',
      'getSelectedItems',
    ]),

    // @todo Consider if it's possible to move these counts to Vuex
    // @see https://jumpcloud.atlassian.net/browse/DE-2534
    currentActiveUsersCount() {
      const activeCount = get(this.getItemDetails(this.orgId), 'systemUsersActivatedCount', 0);
      return NumberSeparatorUtil.thousandSeparator(activeCount);
    },

    currentStagedUsersCount() {
      const stagedCount = get(this.getItemDetails(this.orgId), 'systemUsersStagedCount', 0);
      return NumberSeparatorUtil.thousandSeparator(stagedCount);
    },

    currentSuspendedUsersCount() {
      const suspendedCount = get(this.getItemDetails(this.orgId), 'systemUsersSuspendedCount', 0);
      return NumberSeparatorUtil.thousandSeparator(suspendedCount);
    },

    currentUsersCount() {
      const systemUsersCount = get(this.getItemDetails(this.orgId), 'systemUsersCount', 0);
      return NumberSeparatorUtil.thousandSeparator(systemUsersCount);
    },

    stateComponents() {
      const states = [
        {
          count: this.currentUsersCount,
          label: allUsers,
          type: allUsers,
        }, {
          count: this.currentStagedUsersCount,
          label: this.getHeaderText(staged),
          type: staged,
        }, {
          count: this.currentActiveUsersCount,
          label: 'Active',
          type: activated,
        }, {
          count: this.currentSuspendedUsersCount,
          label: this.getHeaderText(suspended),
          type: suspended,
        },
      ];

      return states;
    },
  },

  mounted() {
    this.isBannerDismissed();
  },

  settingsRoute: '/users/settings',

  methods: {
    ...mapActions(notificationModule, [
      'triggerErrorNotification',
    ]),

    ...mapActions(systemUserCollection, [
      'deleteFilter',
      'fetchCollection',
      'setFilter',
      'setSearchTerm',
      'syncTotalCount',
      'unselectItems',
    ]),

    dismissSettingsBanner() {
      const { getValue, setItem } = this.$options.LocalStorageService;
      if (!getValue(LocalStorageConsts.userStateManualBanner)) {
        setItem(LocalStorageConsts.userStateManualBanner, 'seen');
      }
    },

    getHeaderText(type) {
      return capitalize(type);
    },

    /**
     * Remove all filters, searches and clear selections before switching tabs.
     * @see https://jumpcloud.atlassian.net/browse/DE-1809
     */
    async handleClick(type, index) {
      const message = 'Unable to update Users selection.';
      this.showLoadingOverlay = true;

      this.getAppliedFilters.forEach(async (key) => {
        await this.deleteFilter({ filterName: key.name });
      });
      this.setSearchTerm({ searchTerm: '' });
      this.unselectItems({ ids: this.getSelectedItems });

      try {
        const filter = {
          name: 'state',
          operator: 'eq',
          value: type,
        };

        if (type !== allUsers) {
          await this.setFilter({ filter });
        }

        // Update the system user collection and get the list of `next events` for the
        // items that are in the filtered list
        await this.fetchCollection();

        /**
         * The User State tabs are essentially a custom filter based on the user state.
         * Because of this, some of the normal collection actions don't quite work as expected,
         * particularly related to the counts for the collection.
         *
         * In this case, set the `totalCount` and `totalCountOfAllItems` equal for the
         * collection on the main users page; this is necessary to maintain the correct user
         * count for the `X of Y users selected` message in the action bar for each user state.
         * If this is not done, the `Y` value will be set to whatever value was set during
         * the most recent `refreshCollection` call.
         */
        await this.syncTotalCount();

        // Set the current tab
        this.activeSelection = index;
      } catch (err) {
        this.triggerErrorNotification({ title: 'Error', message });
      } finally {
        this.showLoadingOverlay = false;
      }
    },

    handleClose() {
      this.showNotification = false;
      this.dismissSettingsBanner();
    },

    isActiveSelection(index) {
      return this.activeSelection === index;
    },

    isBannerDismissed() {
      const { getValue } = this.$options.LocalStorageService;
      if (getValue(LocalStorageConsts.userStateManualBanner)) {
        this.showNotification = false;
      } else {
        this.showNotification = true;
      }
    },
  },
  LocalStorageService,

  notifications,
};
</script>

<style module>
@value (jcGrey300) from '~css/brand-colors.css';

.container {
  display: grid;
  margin: 10px 0;
}

.headerText.headerText {
  color: var(--jc-color-neutral-text-base);
  cursor: pointer;
  text-decoration: none !important;
}

.activeHeaderText {
  border-bottom: 2px solid var(--jcPrimary);
}

.header {
  font-size: 1.5rem;
  font-weight: 700;
  list-style: none;
  margin-top: 10px !important;
  padding: 0 0 0 20px;
}

.list {
  align-items: center;
  display: flex;
}

.list div:last-child {
  margin-left: auto;
  padding-bottom: var(--jc-spacer-x-small);
  padding-right: var(--jc-spacer-medium);
}

.count {
  align-self: center;
  background-color: jcGrey300;
  border-radius: 10px;
  font-size: 0.75em;
  font-weight: 700;
  margin: 0 0 0 7px;
  padding: 2px 5px;
}

.notification {
  margin: var(--jc-spacer-medium);
  width: 98% !important;
}
</style>
